import './PageTabs.css';

import { Dropdown, Modal, Tabs, Tooltip, message, theme } from 'antd';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolvePageTabActiveKey } from '@/hooks/useSyncedPageTabs';

/** 从 selection 实践 tab key 中提取实践 ID */
function getCaseId(tabKey) {
    const m = tabKey.match(/^\/selection\/cases\/(?:edit\/)?([^/]+)/);
    return m?.[1] ?? null;
}

function isCaseTab(tabKey) {
    return /^\/selection\/cases\/(?:edit\/)?[^/]+\/?$/.test(tabKey);
}

export default function PageTabs({ tabs, onTabsChange }) {
    const { token } = theme.useToken();
    const navigate = useNavigate();
    const { pathname, state: locationState } = useLocation();

    /** 侧栏与主区分界处留白尽量小；主内容区仍由 MainLayout Content 的 margin 控制 */
    const tabsNavBarStyle = useMemo(() => ({ margin: 0, flexShrink: 0 }), [token.paddingXS]);

    const pageTabsTokenStyle = useMemo(
        () => ({
            flexShrink: 0,
            minHeight: token.controlHeightLG,
            overflow: 'hidden',
            '--pt-bg-inactive': token.colorFillTertiary,
            '--pt-bg-inactive-hover': token.colorFillSecondary,
            '--pt-text-inactive': token.colorTextSecondary,
            '--pt-text-inactive-hover': token.colorText,
            '--pt-bg-active': token.colorPrimaryBg,
            '--pt-text-active': token.colorPrimaryText,
            '--pt-border': token.colorBorderSecondary,
            '--pt-radius': `${token.borderRadiusSM}px`,
            '--pt-container-bg': token.colorBgContainer,
        }),
        [token],
    );

    const closeTab = useCallback(
        key => {
            const idx = tabs.findIndex(t => t.key === key);
            if (idx === -1) return;
            const next = tabs.filter(t => t.key !== key);
            onTabsChange(next);
            if (key !== pathname) return;
            const fallback = next.length === 0 ? { key: '/' } : next[Math.min(idx, next.length - 1)];
            if (fallback.state) {
                navigate(fallback.key, { state: fallback.state });
            } else {
                navigate(fallback.key);
            }
        },
        [tabs, pathname, navigate, onTabsChange],
    );

    const closeAllTabs = useCallback(() => {
        const home = tabs.find(t => t.key === '/') ?? { key: '/', label: '首页' };
        onTabsChange([home]);
        if (pathname !== '/') navigate('/');
    }, [tabs, pathname, navigate, onTabsChange]);

    const handleEdit = useCallback(
        (targetKey, action) => {
            if (action === 'remove' && typeof targetKey === 'string') {
                closeTab(targetKey);
            }
        },
        [closeTab],
    );

    const handleChange = useCallback(
        key => {
            const target = tabs.find(t => t.key === key);
            if (target?.state) {
                navigate(key, { state: target.state });
            } else {
                navigate(key);
            }
        },
        [tabs, navigate],
    );

    const orderedTabs = useMemo(() => {
        const rest = tabs.filter(t => t.key !== '/');
        const home = tabs.find(t => t.key === '/') ?? { key: '/', label: '首页' };
        return [home, ...rest];
    }, [tabs]);

    const activeKey = useMemo(() => resolvePageTabActiveKey(orderedTabs, pathname, locationState), [orderedTabs, pathname, locationState]);

    const closableCount = useMemo(() => orderedTabs.filter(t => t.key !== '/').length, [orderedTabs]);

    const contextMenu = useMemo(
        () => ({
            items: [{ key: 'close-all', label: '关闭全部页签', danger: true, disabled: closableCount === 0 }],
            onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                if (key === 'close-all') {
                    Modal.confirm({
                        title: '关闭全部页签',
                        content: '确定关闭所有已打开页面？',
                        okText: '关闭全部',
                        okButtonProps: { danger: true },
                        cancelText: '取消',
                        centered: true,
                        onOk: () => closeAllTabs(),
                    });
                }
            },
        }),
        [closableCount, closeAllTabs],
    );

    const items = useMemo(
        () =>
            orderedTabs.map(tab => {
                const isWorkspaceTab = tab.workspaceId != null;
                const isCase = isCaseTab(tab.key);
                const caseId = isCase ? getCaseId(tab.key) : null;
                const caseName = isCase ? tab.state?.caseName : null;
                const caseTooltip =
                    isCase && caseName ? (
                        <span key={`tt-${tab.key}`}>
                            {caseName} #{caseId}{' '}
                            <i
                                className="bi bi-copy page-tabs__copy-icon"
                                role="button"
                                tabIndex={0}
                                aria-label="复制实践ID"
                                onClick={e => {
                                    e.stopPropagation();
                                    navigator.clipboard
                                        ?.writeText(`${caseName} #${caseId}`)
                                        .then(() => message.success('复制成功'))
                                        .catch(() => message.error('复制失败'));
                                }}
                            />
                        </span>
                    ) : null;
                const workspaceTooltip = isWorkspaceTab ? (
                    <span key={`tt-${tab.key}`}>
                        {tab.title || tab.label} #{tab.workspaceId}{' '}
                        <i
                            className="bi bi-copy page-tabs__copy-icon"
                            role="button"
                            tabIndex={0}
                            aria-label="复制项目ID"
                            onClick={e => {
                                e.stopPropagation();
                                navigator.clipboard
                                    ?.writeText(`${tab.title || tab.label} #${tab.workspaceId}`)
                                    .then(() => message.success('复制成功'))
                                    .catch(() => message.error('复制失败'));
                            }}
                        />
                    </span>
                ) : null;

                const labelEl = (
                    <span
                        key={tab.key}
                        className="page-tabs__label"
                        role="presentation"
                        onDoubleClick={e => {
                            e.stopPropagation();
                            if (tab.key !== '/') closeTab(tab.key);
                        }}
                    >
                        {tab.label}
                    </span>
                );
                const hasSecondaryTooltip = !isWorkspaceTab && !caseTooltip && tab.title != null && tab.title !== tab.label;
                return {
                    key: tab.key,
                    label: isWorkspaceTab ? (
                        <Tooltip title={workspaceTooltip} mouseEnterDelay={0.05} placement="top" getPopupContainer={() => document.body}>
                            {labelEl}
                        </Tooltip>
                    ) : caseTooltip ? (
                        <Tooltip title={caseTooltip} mouseEnterDelay={0.05} placement="top" getPopupContainer={() => document.body}>
                            {labelEl}
                        </Tooltip>
                    ) : hasSecondaryTooltip ? (
                        <Tooltip title={tab.title} mouseEnterDelay={0.05} placement="top" getPopupContainer={() => document.body}>
                            {labelEl}
                        </Tooltip>
                    ) : (
                        labelEl
                    ),
                    closable: tab.key !== '/',
                    children: null,
                };
            }),
        [orderedTabs, closeTab],
    );

    return (
        <Dropdown menu={contextMenu} trigger={['contextMenu']} getPopupContainer={() => document.body}>
            <div className="page-tabs" style={pageTabsTokenStyle}>
                <Tabs
                    type="editable-card"
                    hideAdd
                    activeKey={activeKey}
                    items={items}
                    onChange={handleChange}
                    onEdit={handleEdit}
                    removeIcon={<i className="bi bi-x-circle-fill page-tabs__remove-icon" aria-hidden />}
                    tabBarStyle={tabsNavBarStyle}
                    styles={{ root: { overflow: 'hidden' }, content: { display: 'none' } }}
                />
            </div>
        </Dropdown>
    );
}
